import { supabase } from '@/lib/supabase'

export async function getStripeKeys(): Promise<{ publishable: string | null; has_secret: boolean }> {
  const { data, error } = await supabase.rpc('get_stripe_keys')
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  return { publishable: row?.publishable ?? null, has_secret: !!row?.has_secret }
}

export async function setStripeKeys(publishable: string, secret: string): Promise<void> {
  const { error } = await supabase.rpc('set_stripe_keys', { p_publishable: publishable, p_secret: secret })
  if (error) throw error
}

export async function setStripeWebhookSecret(secret: string): Promise<void> {
  const { error } = await supabase.rpc('set_stripe_webhook_secret', { p_secret: secret })
  if (error) throw error
}

export async function hasStripeWebhookSecret(): Promise<boolean> {
  const { data, error } = await supabase.rpc('has_stripe_webhook_secret')
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  return !!row?.has_webhook
}

export async function getVercelConfig(): Promise<{ project_id: string | null; has_token: boolean }> {
  const { data, error } = await supabase.rpc('get_vercel_config')
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  return { project_id: row?.project_id ?? null, has_token: !!row?.has_token }
}

export async function setVercelConfig(project_id: string, token: string): Promise<void> {
  const { error } = await supabase.rpc('set_vercel_config', { p_project_id: project_id, p_token: token })
  if (error) throw error
}

export async function getTermsText(): Promise<string> {
  const { data, error } = await supabase.rpc('get_terms_text')
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  return (row?.text as string) || ''
}

export async function setTermsText(text: string): Promise<void> {
  const { error } = await supabase.rpc('set_terms_text', { p_text: text })
  if (error) throw error
}

export async function getOpportunitiesNoticeText(): Promise<string> {
  const { data, error } = await supabase.rpc('get_opportunities_notice_text')
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  return (row?.text as string) || ''
}

export async function setOpportunitiesNoticeText(text: string): Promise<void> {
  const { error } = await supabase.rpc('set_opportunities_notice_text', { p_text: text })
  if (error) throw error
}

export async function getMaintenanceMode(): Promise<boolean> {
  const { data, error } = await supabase.rpc('get_maintenance_mode')
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  return !!row?.maintenance
}

export async function setMaintenanceMode(on: boolean): Promise<void> {
  const { error } = await supabase.rpc('set_maintenance_mode', { p_on: on })
  if (error) throw error
}

export type BillingManageMode = 'portal' | 'inline'
export async function getBillingManageMode(): Promise<BillingManageMode> {
  const { data, error } = await supabase.rpc('get_billing_manage_mode')
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  const mode = String(row?.mode || 'portal').toLowerCase()
  return mode === 'inline' ? 'inline' : 'portal'
}

export async function setBillingManageMode(mode: BillingManageMode): Promise<void> {
  const { error } = await supabase.rpc('set_billing_manage_mode', { p_mode: mode })
  if (error) throw error
}

// Stripe Billing Portal configuration stored in app_settings
export type StripePortalEnvConfig = { configuration_id: string | null; return_url: string | null }
export type StripePortalConfig = { test: StripePortalEnvConfig; live: StripePortalEnvConfig }

export async function getStripePortalConfig(): Promise<StripePortalConfig> {
  const keys = [
    'billing.portal.configuration_id',
    'billing.portal.return_url',
    'billing.portal.configuration_id.test',
    'billing.portal.return_url.test',
    'billing.portal.configuration_id.live',
    'billing.portal.return_url.live',
  ]
  const { data, error } = await supabase.from('app_settings').select('key,value').in('key', keys)
  if (error) throw error
  const map = new Map<string, string>((data || []).map((r: any) => [r.key, r.value as string]))
  const test: StripePortalEnvConfig = {
    configuration_id: map.get('billing.portal.configuration_id.test') || map.get('billing.portal.configuration_id') || null,
    return_url: map.get('billing.portal.return_url.test') || map.get('billing.portal.return_url') || null,
  }
  const live: StripePortalEnvConfig = {
    configuration_id: map.get('billing.portal.configuration_id.live') || map.get('billing.portal.configuration_id') || null,
    return_url: map.get('billing.portal.return_url.live') || map.get('billing.portal.return_url') || null,
  }
  return { test, live }
}

export async function setStripePortalConfig(cfg: StripePortalConfig): Promise<void> {
  const rows: Array<{ key: string; value: string | null }> = [
    { key: 'billing.portal.configuration_id.test', value: cfg.test.configuration_id || null },
    { key: 'billing.portal.return_url.test', value: cfg.test.return_url || null },
    { key: 'billing.portal.configuration_id.live', value: cfg.live.configuration_id || null },
    { key: 'billing.portal.return_url.live', value: cfg.live.return_url || null },
  ]
  const payload = rows.map(r => ({ key: r.key, value: r.value ?? '' }))
  const { error } = await supabase.from('app_settings').upsert(payload, { onConflict: 'key' })
  if (error) throw error
}

// Stripe API keys por ambiente (app_settings)
export type StripeKeysEnv = { publishable: string; secret: string }
export type StripeMode = 'test' | 'live'
export type StripeKeysConfig = { test: StripeKeysEnv; live: StripeKeysEnv; mode: 'test'|'live' }

export async function getStripeKeysEnv(): Promise<StripeKeysConfig> {
  const keys = [
    'stripe.publishable_key.test', 'stripe.secret_key.test',
    'stripe.publishable_key.live', 'stripe.secret_key.live',
    'stripe.mode',
  ]
  const { data, error } = await supabase.from('app_settings').select('key,value').in('key', keys)
  if (error) throw error
  const map = new Map<string,string>((data||[]).map((r:any)=>[r.key, String(r.value||'')]))
  return {
    test: { publishable: map.get('stripe.publishable_key.test') || '', secret: map.get('stripe.secret_key.test') || '' },
    live: { publishable: map.get('stripe.publishable_key.live') || '', secret: map.get('stripe.secret_key.live') || '' },
    mode: (map.get('stripe.mode') === 'live' ? 'live' : 'test'),
  }
}

export async function setStripeKeysEnv(cfg: StripeKeysConfig): Promise<void> {
  const rows: Array<{ key: string; value: string | null }> = [
    { key: 'stripe.publishable_key.test', value: cfg.test.publishable || '' },
    { key: 'stripe.secret_key.test', value: cfg.test.secret || '' },
    { key: 'stripe.publishable_key.live', value: cfg.live.publishable || '' },
    { key: 'stripe.secret_key.live', value: cfg.live.secret || '' },
    { key: 'stripe.mode', value: cfg.mode },
  ]
  const payload = rows.map(r => ({ key: r.key, value: r.value ?? '' }))
  const up = await supabase.from('app_settings').upsert(payload, { onConflict: 'key' })
  if (up.error) throw up.error
  // Também atualiza as chaves genéricas usadas pelos serviços/edges hoje
  const active = cfg.mode === 'live' ? cfg.live : cfg.test
  const mirror = await supabase.from('app_settings').upsert([
    { key: 'stripe.publishable_key', value: active.publishable || '' },
    { key: 'stripe.secret_key', value: active.secret || '' },
  ], { onConflict: 'key' })
  if (mirror.error) throw mirror.error
}

// Stripe modes por ambiente (opcional): permite escolher modos distintos para localhost e site
export async function getStripeModes(): Promise<{ mode?: StripeMode; mode_local?: StripeMode; mode_site?: StripeMode }> {
  const keys = ['stripe.mode', 'stripe.mode.local', 'stripe.mode.site']
  const { data, error } = await supabase.from('app_settings').select('key,value').in('key', keys)
  if (error) throw error
  const map = new Map<string,string>((data||[]).map((r:any)=>[r.key, String(r.value||'')]))
  const norm = (v?: string|null): StripeMode|undefined => (v === 'live' ? 'live' : (v === 'test' ? 'test' : undefined))
  return {
    mode: norm(map.get('stripe.mode')||''),
    mode_local: norm(map.get('stripe.mode.local')||''),
    mode_site: norm(map.get('stripe.mode.site')||''),
  }
}

export async function setStripeModes(payload: { mode?: StripeMode; mode_local?: StripeMode; mode_site?: StripeMode }): Promise<void> {
  const rows: Array<{ key: string; value: string | null }> = []
  if (payload.mode) rows.push({ key: 'stripe.mode', value: payload.mode })
  if (payload.mode_local) rows.push({ key: 'stripe.mode.local', value: payload.mode_local })
  if (payload.mode_site) rows.push({ key: 'stripe.mode.site', value: payload.mode_site })
  if (rows.length > 0) {
    const { error } = await supabase.from('app_settings').upsert(rows.map(r=>({ key: r.key, value: r.value ?? '' })), { onConflict: 'key' })
    if (error) throw error
  }
  // Mantém espelho genérico coerente (usa modo do site se definido, senão modo padrão)
  try {
    const keys = [
      'stripe.publishable_key.test', 'stripe.secret_key.test',
      'stripe.publishable_key.live', 'stripe.secret_key.live',
    ]
    const { data } = await supabase.from('app_settings').select('key,value').in('key', keys)
    const map = new Map<string,string>((data||[]).map((r:any)=>[r.key, String(r.value||'')]))
    const modeSite = payload.mode_site || payload.mode || 'test'
    const pub = modeSite === 'live' ? (map.get('stripe.publishable_key.live')||'') : (map.get('stripe.publishable_key.test')||'')
    const sec = modeSite === 'live' ? (map.get('stripe.secret_key.live')||'') : (map.get('stripe.secret_key.test')||'')
    await supabase.from('app_settings').upsert([
      { key: 'stripe.publishable_key', value: pub },
      { key: 'stripe.secret_key', value: sec },
    ], { onConflict: 'key' })
  } catch {}
}

// ---------- Planos: allowlist para Link Exclusivo ----------

const KEY_EXCLUSIVE_ALLOWED_PLANS = 'plans.exclusive_allowed_plan_ids'
const KEY_EXCLUSIVE_POPULAR_PLAN_ID = 'plans.exclusive_popular_plan_id'

export async function getExclusiveAllowedPlanIds(): Promise<string[]> {
  try {
    const { data, error } = await supabase
      .from('app_settings')
      .select('value')
      .eq('key', KEY_EXCLUSIVE_ALLOWED_PLANS)
      .maybeSingle()
    if (error) throw error
    const raw = (data?.value as string) || ''
    if (!raw) return []
    try { const arr = JSON.parse(raw); return Array.isArray(arr) ? arr.map((s:any)=>String(s)) : [] } catch {}
    return raw.split(',').map(s=>s.trim()).filter(Boolean)
  } catch { return [] }
}

export async function setExclusiveAllowedPlanIds(ids: string[]): Promise<void> {
  const val = JSON.stringify(Array.from(new Set((ids||[]).map(String))))
  const { error } = await supabase
    .from('app_settings')
    .upsert({ key: KEY_EXCLUSIVE_ALLOWED_PLANS, value: val }, { onConflict: 'key' })
  if (error) throw error
}

export async function getExclusivePopularPlanId(): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('app_settings')
      .select('value')
      .eq('key', KEY_EXCLUSIVE_POPULAR_PLAN_ID)
      .maybeSingle()
    if (error) throw error
    const raw = (data?.value as string) || ''
    const id = raw.trim()
    return id ? id : null
  } catch { return null }
}

export async function setExclusivePopularPlanId(id: string | null): Promise<void> {
  const { error } = await supabase
    .from('app_settings')
    .upsert({ key: KEY_EXCLUSIVE_POPULAR_PLAN_ID, value: (id || '') }, { onConflict: 'key' })
  if (error) throw error
}
